/**
 * CSV parsing and generation utilities
 * Handles proper escaping of CSV values (quotes, commas, newlines)
 */

/**
 * Escape a CSV field value
 * Wraps in quotes if contains comma, newline, or quote
 * Escapes internal quotes by doubling them
 */
export function escapeCSV(value: string | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }
  
  const str = String(value);
  
  // If contains comma, newline, or quote, wrap in quotes and escape quotes
  if (str.includes(',') || str.includes('\n') || str.includes('\r') || str.includes('"')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  
  return str;
}

/**
 * Unescape a CSV field value
 * Removes surrounding quotes and unescapes doubled quotes
 */
export function unescapeCSV(value: string): string {
  if (!value) return '';
  
  // Remove surrounding quotes if present
  if (value.startsWith('"') && value.endsWith('"')) {
    value = value.slice(1, -1);
    // Unescape doubled quotes
    value = value.replace(/""/g, '"');
  }
  
  return value;
}

/**
 * Parse a single CSV line, handling quoted fields
 */
export function parseCSVLine(line: string): string[] {
  const fields: string[] = [];
  let currentField = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        currentField += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator
      fields.push(unescapeCSV(currentField));
      currentField = '';
    } else {
      currentField += char;
    }
  }
  
  // Add last field
  fields.push(unescapeCSV(currentField));
  
  return fields;
}

/**
 * Parse CSV content into rows and columns
 */
export function parseCSV(content: string): string[][] {
  const lines = content.split(/\r?\n/).filter(line => line.trim().length > 0);
  return lines.map(line => parseCSVLine(line));
}

/**
 * Generate CSV content from rows
 */
export function generateCSV(rows: string[][]): string {
  return rows.map(row => 
    row.map(field => escapeCSV(field)).join(',')
  ).join('\n');
}

